import React, { useLayoutEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { Tab } from '@material-ui/core'
import { NavLink, useHistory, useLocation } from 'react-router-dom'
import { parseStoreAccountPathFromUrl } from '../../../hooks/routeHooks'
import { ReactiveTabs } from '../../../components/ReactiveTabs'
import { makeMpCardAccountUrl, mpCardRoutes } from '../../../common/mpCardRoutes'
import { useAccountsTabs } from '../../../hooks/useAccountsTabs'
import { useLogic, useStore } from '../../../hooks/storeHook'
import { FilterBar } from './components/filterBar'
import { TabTitle } from './components/tabTitle'
import { integrationKeyByType } from '../../../types/integrationTypeUtils'
import { IServerTableData, TableColumn } from '../../../types/tableTypes'
import { ExternalProductModel } from '../../../server/mpsklad_core/Models/ExternalProductModel'
import { Header } from './components/header'
import { MyServerTable } from '../../../components/MyServerTable'

export type ProductsTableProps<
  TProducts extends IServerTableData<TRow, TColumnId>,
  TRow extends ExternalProductModel,
  TColumnId extends string> = {
  products: TProducts
  columns: TableColumn<TColumnId, TRow>[]
}

export const ProductsTable = observer(
  <TProducts extends IServerTableData<TRow, TColumnId>,
    TRow extends ExternalProductModel,
    TColumnId extends string>({products, columns}: ProductsTableProps<TProducts, TRow, TColumnId>) => {
    const store = useStore()
    const history = useHistory()
    const location = useLocation()
    const accountRoute = parseStoreAccountPathFromUrl(location.pathname)
    const accountTabs = useAccountsTabs(true)
    const {tryGetAccount} = useLogic()
    const ignoreTabChangeRef = useRef(true)

    const {setHomeNav} = store

    useLayoutEffect(() => {
      ignoreTabChangeRef.current = true

      if (accountRoute.integration != null && accountRoute.accountId != null) {
        setHomeNav(accountRoute.integration, accountRoute.accountId)
      }
    }, [accountRoute.integration, accountRoute.accountId, setHomeNav])

    useLayoutEffect(() => {
      ignoreTabChangeRef.current = false
    }, [location.pathname, accountTabs.length])

    const integrationKey =
      accountRoute.integration != null ? integrationKeyByType[accountRoute.integration] : undefined

    const account =
      accountRoute.accountId != null && integrationKey != null
      ? tryGetAccount(accountRoute.accountId, integrationKey)
      : undefined

    const onTabChange = (newTab: number, event: React.ChangeEvent<{}>) => {
      if (
        ignoreTabChangeRef.current
        || !event.isTrusted
        || accountRoute.integration == null
        || accountRoute.accountId == null
      ) {
        return
      }

      if (newTab < 0 || newTab >= accountTabs.length) {
        throw new Error(`Unprocessed products tab: ${newTab}`)
      }

      const selectedTab = accountTabs[newTab]
      const targetUrl = makeMpCardAccountUrl(selectedTab.integrationType, selectedTab.id)

      if (location.pathname === targetUrl) {
        return
      }

      setHomeNav(selectedTab.integrationType, selectedTab.id)
      history.push(targetUrl)
    }

    const handleUnsetAccount = () => {
      store.unsetHomeNavAccount()
    }

    const AddAccountButton = <Tab
      disableRipple label={'Добавить магазин'} component={NavLink}
      to={mpCardRoutes.settings.new.route({integration: 'Wildberries'})} onClick={handleUnsetAccount}/>

    if (
      accountRoute.integration == null
      || accountRoute.accountId == null
      || !account
    ) {
      return AddAccountButton
    }

    const activeTabIndex = accountTabs.findIndex(
      t => t.id === accountRoute.accountId && t.integrationType === accountRoute.integration
    )

    return (
      <ReactiveTabs
        value={activeTabIndex < 0 ? false : activeTabIndex}
        tabs={accountTabs.map((t, index) => ({
          value: index,
          title: <TabTitle account={t} key={t.id}/>
        }))}
        onChange={onTabChange}
        appBarProps={{elevation: 1}}
        AfterTabsInlineComponent={() => AddAccountButton}>
        <>
          <div className="body_wrapper">
            {/*TODO: Remove ts-ignore after implementing this functional in prod*/}
            {/*@ts-ignore*/}
            <FilterBar<TProducts, TRow, TColumnId> account={account} products={products}/>
          </div>

          <MyServerTable
            table={products}
            columns={columns}
            HeadComponent={() => <Header columns={columns} products={products} account={account}/>}
            labelNoRows={'Нет товаров'}
            labelNotFound={'Товары не найдены'}
            hideTopPagination={true}
          />
        </>
      </ReactiveTabs>
    )
  })
